// Ocean Insight Pro Mobile - Dashboard Screen
// Main overview screen showing weather, fishing conditions, and alerts

import React, { useEffect, useState } from 'react';
import { View, ScrollView, StyleSheet, RefreshControl, TouchableOpacity } from 'react-native';
import {
  Card,
  Title,
  Paragraph,
  Button,
  useTheme,
  ActivityIndicator,
  Portal,
  Modal,
} from 'react-native-paper';
import { MaterialIcons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';

// Import Redux actions and types
import { RootState, AppDispatch } from '../store';
import { MarineForecastBrief } from '../components/MarineForecastBrief';
import { fetchCurrentWeather, fetchHourlyWeatherForecast, fetchWeatherAlerts, fetchWeatherForecast } from '../store/weatherSlice';
import { loadLocationPreferences } from '../store/userSlice';
// Ocean data fetching removed - only loads when user goes to map
import { FISHING_REGIONS } from '../services/mockData';
import type { RootStackParamList, WeatherAlert, GeoCoordinate, WeatherForecast } from '../types';
import { locationService } from '../services/locationService';
import { getSeaState } from '../utils/seaState';
import { formatWaveHeight, formatWavePeriod, hasMarineData, marineCoverageDays } from '../utils/marineData';

type NavigationProp = StackNavigationProp<RootStackParamList>;

export default function DashboardScreen() {
  const theme = useTheme();
  const navigation = useNavigation<NavigationProp>();
  const dispatch = useDispatch<AppDispatch>();
  const [userLocation, setUserLocation] = useState<GeoCoordinate | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [selectedDayForecast, setSelectedDayForecast] = useState<WeatherForecast | null>(null);
  const [showDayDetailModal, setShowDayDetailModal] = useState(false);

  // Get state from Redux
  const {
    current: currentWeather,
    forecast,
    hourlyForecast,
    alerts,
    loading: weatherLoading,
  } = useSelector((state: RootState) => state.weather);
  const { data: oceanographicData, loading: oceanographicLoading } = useSelector(
    (state: RootState) => state.oceanographic
  );
  const { locationPreferences } = useSelector((state: RootState) => state.user);

  // Load location preferences on mount
  useEffect(() => {
    dispatch(loadLocationPreferences());
  }, [dispatch]);

  // Get user's location when preferences change
  useEffect(() => {
    getUserLocation();
  }, [locationPreferences]);

  // Load data when location is available
  useEffect(() => {
    if (userLocation) {
      loadDashboardData();
    }
  }, [userLocation]);

  const getUserLocation = async () => {
    try {
      setLocationError(null);
      
      console.log('Location preferences:', {
        useCurrentLocation: locationPreferences.useCurrentLocation,
        savedLocation: locationPreferences.savedLocation,
      });
      
      // Check if user has saved location preference
      if (!locationPreferences.useCurrentLocation && locationPreferences.savedLocation) {
        // Use saved location
        console.log('Using saved location:', locationPreferences.savedLocation.name);
        setUserLocation(locationPreferences.savedLocation.coordinates);
        return;
      }
      
      // Otherwise get current location
      console.log('Using current GPS location');
      try {
        const location = await locationService.getCurrentLocation();
        if (location) {
          setUserLocation({
            latitude: location.latitude,
            longitude: location.longitude,
          });
        } else {
          // Use default location if permission denied
          console.log('Location permission denied, using default location');
          setUserLocation({
            latitude: 40.7128,
            longitude: -74.0060,
          });
        }
      } catch (locError) {
        console.error('Location service error:', locError);
        // Use default location on error
        setUserLocation({
          latitude: 40.7128,
          longitude: -74.0060,
        });
      }
    } catch (error) {
      console.error('Error getting location:', error);
      setLocationError('Unable to get current location');
      // Fallback to saved location or default location
      const fallbackLocation =
        locationPreferences.savedLocation?.coordinates ||
        FISHING_REGIONS.gulfOfMexico.destin;
      setUserLocation(fallbackLocation);
    }
  };

  const loadDashboardData = (): void => {
    if (!userLocation) return;

    // Fetch current weather, forecast and alerts using actual user location
    void dispatch(fetchCurrentWeather(userLocation));
    void dispatch(fetchWeatherForecast(userLocation));
    void dispatch(fetchHourlyWeatherForecast(userLocation));
    void dispatch(fetchWeatherAlerts(userLocation));

    // Don't fetch oceanographic data here - only loads when user goes to map and selects a layer
  };

  const onRefresh = () => {
    loadDashboardData();
  };

  const getWeatherIcon = (conditions: string) => {
    if (conditions.includes('Clear')) return 'wb-sunny';
    if (conditions.includes('Cloudy')) return 'cloud';
    if (conditions.includes('Rain')) return 'grain';
    if (conditions.includes('Thunderstorms')) return 'flash-on';
    if (conditions.includes('Fog')) return 'blur-on';
    return 'wb-cloudy';
  };



  const isLoading = weatherLoading || oceanographicLoading;
  const selectedDayHourlyForecast = selectedDayForecast
    ? hourlyForecast.filter(hour =>
      hour.timestamp.slice(0, 10) === selectedDayForecast.timestamp.slice(0, 10) &&
      hour.location.latitude === selectedDayForecast.location.latitude &&
      hour.location.longitude === selectedDayForecast.location.longitude
    )
    : [];

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      refreshControl={<RefreshControl refreshing={isLoading} onRefresh={onRefresh} />}
    >
      {/* Location Status */}
      {locationError && (
        <Card style={[styles.card, { backgroundColor: theme.colors.errorContainer }]}>
          <Card.Content style={styles.errorAlert}>
            <MaterialIcons name="location-off" size={20} color={theme.colors.onErrorContainer} />
            <Paragraph style={{ color: theme.colors.onErrorContainer, marginLeft: 8 }}>
              {locationError} - Using default location
            </Paragraph>
          </Card.Content>
        </Card>
      )}

      {/* Current Weather Conditions */}
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.weatherHeader}>
            <View style={styles.weatherInfo}>
              <Title>Current Conditions</Title>
              {userLocation && (
                <Paragraph style={styles.locationLabel}>
                  {!locationPreferences.useCurrentLocation && locationPreferences.savedLocation
                    ? locationPreferences.savedLocation.name
                    : `${userLocation.latitude.toFixed(2)}°, ${userLocation.longitude.toFixed(2)}°`}
                </Paragraph>
              )}
              {currentWeather && (
                <View style={styles.weatherDetails}>
                  <MaterialIcons
                    name={getWeatherIcon(currentWeather.weather.conditions)}
                    size={32}
                    color={theme.colors.primary}
                  />
                  <View style={styles.weatherText}>
                    <Paragraph style={styles.temperature}>
                      {Math.round(currentWeather.weather.temperature)}°F
                    </Paragraph>
                    <Paragraph style={styles.conditions}>
                      {currentWeather.weather.conditions}
                    </Paragraph>
                  </View>
                </View>
              )}
            </View>
            <Button
              mode="outlined"
              onPress={() => {
                // FIX: Ensure location exists before navigating
                if (currentWeather?.location) {
                  navigation.navigate('WeatherDetails', { location: currentWeather.location });
                }
              }}
              compact
              disabled={!currentWeather?.location}
            >
              Details
            </Button>
          </View>

          {currentWeather && (
            <View style={styles.weatherGrid}>
              <View style={styles.weatherItem}>
                <MaterialIcons name="air" size={16} color={theme.colors.primary} />
                <Paragraph style={styles.weatherValue}>{currentWeather.wind.speed} kt</Paragraph>
                <Paragraph style={styles.weatherLabel}>Wind</Paragraph>
              </View>
              <View style={styles.weatherItem}>
                <MaterialIcons name="waves" size={16} color={theme.colors.primary} />
                <Paragraph style={styles.weatherValue}>{hasMarineData(currentWeather.waves) ? `${currentWeather.waves.height.toFixed(1)} ft` : '—'}</Paragraph>
                <Paragraph style={styles.weatherLabel}>Waves</Paragraph>
              </View>
              <View style={styles.weatherItem}>
                <MaterialIcons name="visibility" size={16} color={theme.colors.primary} />
                <Paragraph style={styles.weatherValue}>
                  {currentWeather.weather.visibility} nm
                </Paragraph>
                <Paragraph style={styles.weatherLabel}>Visibility</Paragraph>
              </View>
              <View style={styles.weatherItem}>
                <MaterialIcons name="compress" size={16} color={theme.colors.primary} />
                <Paragraph style={styles.weatherValue}>
                  {currentWeather.weather.pressure.toFixed(2)} inHg
                </Paragraph>
                <Paragraph style={styles.weatherLabel}>Pressure</Paragraph>
              </View>
            </View>
          )}
        </Card.Content>
      </Card>

      {/* Oceanographic Data Summary */}
      {oceanographicData && (
        <Card style={styles.card}>
          <Card.Content>
            <Title>Ocean Conditions</Title>
            <View style={styles.oceanGrid}>
              {oceanographicData.sst.length > 0 && (
                <View style={styles.weatherItem}>
                  <MaterialIcons name="thermostat" size={16} color={theme.colors.primary} />
                  <Paragraph style={styles.weatherValue}>
                    {oceanographicData.sst[0].temperature.toFixed(1)}°C
                  </Paragraph>
                  <Paragraph style={styles.weatherLabel}>Sea Temp</Paragraph>
                </View>
              )}
              {oceanographicData.chlorophyll.length > 0 && (
                <View style={styles.weatherItem}>
                  <MaterialIcons name="eco" size={16} color={theme.colors.primary} />
                  <Paragraph style={styles.weatherValue}>
                    {oceanographicData.chlorophyll[0].concentration.toFixed(2)}
                  </Paragraph>
                  <Paragraph style={styles.weatherLabel}>Chlorophyll</Paragraph>
                </View>
              )}
              {oceanographicData.currents.length > 0 && (
                <View style={styles.weatherItem}>
                  <MaterialIcons name="trending-up" size={16} color={theme.colors.primary} />
                  <Paragraph style={styles.weatherValue}>
                    {oceanographicData.currents[0].speed.toFixed(1)} kt
                  </Paragraph>
                  <Paragraph style={styles.weatherLabel}>Current</Paragraph>
                </View>
              )}
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Weather Alerts */}
      {alerts.length > 0 && (
        <Card style={[styles.card, { backgroundColor: theme.colors.errorContainer }]}>
          <Card.Content>
            <View style={styles.alertHeader}>
              <Title style={{ color: theme.colors.onErrorContainer }}>Weather Alerts</Title>
              <Button
                mode="outlined"
                onPress={() => {
                  if (userLocation) {
                    navigation.navigate('WeatherDetails', { 
                      location: userLocation
                    });
                  }
                }}
                compact
                style={styles.alertDetailsButton}
                labelStyle={{ color: theme.colors.onErrorContainer }}
                disabled={!userLocation}
              >
                Details
              </Button>
            </View>
            {alerts.slice(0, 2).map((alert: WeatherAlert) => (
              <View key={alert.id} style={styles.alertItem}>
                <MaterialIcons name="warning" size={18} color={theme.colors.onErrorContainer} />
                <View style={styles.alertText}>
                  <Paragraph style={{ color: theme.colors.onErrorContainer, fontWeight: 'bold' }}>
                    {alert.title}
                  </Paragraph>
                  <Paragraph style={{ color: theme.colors.onErrorContainer }}>
                    {alert.description.length > 80
                      ? `${alert.description.substring(0, 80)  }...`
                      : alert.description}
                  </Paragraph>
                </View>
              </View>
            ))}
          </Card.Content>
        </Card>
      )}

      {/* Marine Forecast */}
      <Card style={styles.card}>
        <Card.Content>
          <Title>{forecast.length}-Day Wind · {marineCoverageDays(forecast)}-Day Marine Forecast</Title>

          {forecast && forecast.length > 0 ? (
            <ScrollView 
              horizontal
              showsHorizontalScrollIndicator={true}
              style={styles.forecastScrollView}
              contentContainerStyle={styles.forecastContainer}
            >
              {forecast.map((day, index) => {
                const date = new Date(day.timestamp);
                const seaState = getSeaState(day.waves, day.wind);
                const dayName = index === 0 
                  ? 'Today' 
                  : date.toLocaleDateString('en-US', { weekday: 'short' });
                const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                
                return (
                  <TouchableOpacity
                    key={day.id}
                    style={styles.forecastDay}
                    onPress={() => {
                      setSelectedDayForecast(day);
                      setShowDayDetailModal(true);
                    }}
                  >
                    <View style={styles.forecastHeader}>
                      <Paragraph style={styles.dayName}>{dayName}</Paragraph>
                      <Paragraph style={styles.dayDate}>{dateLabel}</Paragraph>
                      <MaterialIcons
                        name={getWeatherIcon(day.weather.conditions)}
                        size={24}
                        color={theme.colors.primary}
                      />
                    </View>
                    <Paragraph style={styles.forecastTemp}>
                      {Math.round(day.weather.temperature)}°F
                    </Paragraph>
                    <View style={[styles.seaStateBadge, { backgroundColor: seaState.backgroundColor }]}>
                      <MaterialIcons name="waves" size={13} color={seaState.color} />
                      <Paragraph style={[styles.seaStateLabel, { color: seaState.color }]}>{seaState.label}</Paragraph>
                    </View>
                    <View style={styles.forecastDetails}>
                      <View style={styles.forecastItem}>
                        <MaterialIcons name="air" size={14} color={theme.colors.onSurfaceVariant} />
                        <Paragraph style={styles.forecastValue}>
                          {day.wind.minSpeed && day.wind.maxSpeed 
                            ? `${day.wind.minSpeed}-${day.wind.maxSpeed} kt`
                            : `${day.wind.speed} kt`}
                        </Paragraph>
                      </View>
                      <View style={styles.forecastItem}>
                        <MaterialIcons name="waves" size={14} color={theme.colors.onSurfaceVariant} />
                        <Paragraph style={styles.forecastValue}>
                          {hasMarineData(day.waves) && day.waves.minHeight !== undefined && day.waves.maxHeight !== undefined
                            ? `${day.waves.minHeight}-${day.waves.maxHeight} ft`
                            : formatWaveHeight(day.waves)}
                        </Paragraph>
                      </View>
                      <View style={styles.forecastItem}>
                        <MaterialCommunityIcons name="timer-outline" size={14} color={theme.colors.onSurfaceVariant} />
                        <Paragraph style={styles.forecastValue}>
                          {hasMarineData(day.waves) && day.waves.minPeriod !== undefined && day.waves.maxPeriod !== undefined
                            ? `${day.waves.minPeriod}-${day.waves.maxPeriod} s`
                            : formatWavePeriod(day.waves)}
                        </Paragraph>
                      </View>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          ) : (
            <View style={styles.emptyState}>
              {weatherLoading ? (
                <>
                  <ActivityIndicator size="small" />
                  <Paragraph>Loading forecast...</Paragraph>
                </>
              ) : (
                <Paragraph>Unable to load forecast data</Paragraph>
              )}
            </View>
          )}
          
          {/* Check Weather at Any Location button */}
          <Button
            mode="contained-tonal"
            style={{ marginTop: 16 }}
            onPress={() => navigation.navigate('LocationPicker' as any)}
            icon="map-marker-check"
          >
            Check Weather at Any Location
          </Button>
        </Card.Content>
      </Card>

      {/* Quick Actions */}
      <Card style={styles.card}>
        <Card.Content>
          <Title>Quick Actions</Title>
          <View style={styles.actionButtons}>
            <Button
              mode="contained"
              style={styles.actionButton}
              onPress={() => navigation.navigate('Trips')}
            >
              Plan Trip
            </Button>
            <Button
              mode="outlined"
              style={styles.actionButton}
              onPress={() => navigation.navigate('CatchReport', {})}
            >
              Log Catch
            </Button>
          </View>
          <View style={styles.actionButtons}>
            <Button
              mode="outlined"
              style={styles.actionButton}
              onPress={() => navigation.navigate('CatchHistory' as any)}
              icon="history"
            >
              Catch History
            </Button>
          </View>
        </Card.Content>
      </Card>
      
      {/* Day Detail Modal */}
      <Portal>
        <Modal
          visible={showDayDetailModal}
          onDismiss={() => setShowDayDetailModal(false)}
          contentContainerStyle={[styles.modalContent, { backgroundColor: theme.colors.surface }]}
        >
          {selectedDayForecast && (
            <ScrollView 
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ 
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
                flexGrow: 1 
              }}
            >
              <View style={styles.modalHeader}>
                <Title>
                  {new Date(selectedDayForecast.timestamp).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </Title>
                <MaterialIcons
                  name={getWeatherIcon(selectedDayForecast.weather.conditions)}
                  size={32}
                  color={theme.colors.primary}
                />
              </View>
              
              <Paragraph style={styles.conditionsText}>
                {selectedDayForecast.weather.conditions}
              </Paragraph>

              {selectedDayHourlyForecast.length ? (
                <MarineForecastBrief
                  dailyForecast={forecast}
                  hourlyForecast={selectedDayHourlyForecast}
                  initialDate={selectedDayForecast.timestamp.slice(0, 10)}
                  showHeader={false}
                  showDateSelector={false}
                />
              ) : (
                <View style={styles.hourlyLoading}>
                  <ActivityIndicator size="small" />
                  <Paragraph>Loading hourly marine forecast...</Paragraph>
                </View>
              )}
              
              <Button
                mode="outlined"
                onPress={() => setShowDayDetailModal(false)}
                style={{ marginTop: 24 }}
              >
                Close
              </Button>
            </ScrollView>
          )}
        </Modal>
      </Portal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  card: {
    marginBottom: 16,
    elevation: 2,
  },
  errorAlert: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  weatherHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  weatherInfo: {
    flex: 1,
  },
  weatherDetails: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  weatherText: {
    marginLeft: 12,
  },
  temperature: {
    fontSize: 24,
    fontWeight: 'bold',
    lineHeight: 28,
  },
  conditions: {
    fontSize: 14,
    opacity: 0.7,
  },
  weatherGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  oceanGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 12,
  },
  weatherItem: {
    alignItems: 'center',
    flex: 1,
  },
  weatherValue: {
    fontWeight: 'bold',
    marginTop: 4,
  },
  weatherLabel: {
    fontSize: 12,
    opacity: 0.7,
    marginTop: 2,
  },
  alertItem: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  alertText: {
    marginLeft: 8,
    flex: 1,
  },
  alertHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  alertDetailsButton: {
    borderColor: 'rgba(255, 255, 255, 0.5)',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  forecastScrollView: {
    marginTop: 12,
  },
  forecastContainer: {
    flexDirection: 'row',
    gap: 8,
    paddingRight: 8,
  },
  forecastDay: {
    width: 122,
    alignItems: 'center',
    padding: 10,
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: 12,
  },
  forecastHeader: {
    alignItems: 'center',
    marginBottom: 4,
  },
  dayName: {
    fontWeight: 'bold',
    fontSize: 12,
  },
  dayDate: {
    fontSize: 10,
    marginBottom: 4,
    color: '#6B7280',
  },
  forecastTemp: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 6,
  },
  seaStateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 5,
    borderRadius: 6,
    marginBottom: 9,
  },
  seaStateLabel: {
    fontSize: 10,
    lineHeight: 13,
    fontWeight: '700',
    textAlign: 'center',
  },
  forecastDetails: {
    gap: 4,
  },
  forecastItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  forecastValue: {
    fontSize: 11,
    opacity: 0.7,
  },
  locationText: {
    fontSize: 10,
    marginLeft: 2,
    opacity: 0.6,
    flex: 1,
  },
  timeText: {
    fontSize: 10,
    opacity: 0.6,
    fontWeight: 'bold',
  },
  locationLabel: {
    fontSize: 12,
    opacity: 0.6,
    marginTop: 2,
  },
  emptyState: {
    alignItems: 'center',
    padding: 20,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  actionButton: {
    flex: 1,
  },
  modalContent: {
    margin: 20,
    marginBottom: 40,
    padding: 0,
    borderRadius: 12,
    maxHeight: '90%',
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  conditionsText: {
    fontSize: 16,
    textAlign: 'center',
    opacity: 0.8,
  },
  hourlyLoading: {
    alignItems: 'center',
    gap: 8,
    paddingVertical: 32,
  },
});
